/**
 * Timezone-safe date utilities for Europe/Vienna.
 * Fixes off-by-one errors caused by new Date().toISOString() converting to UTC
 * (midnight in Vienna = 23:00 UTC the previous day).
 */

const pad2 = (n) => String(n).padStart(2, '0');

/** Returns today's date as YYYY-MM-DD in Europe/Vienna timezone */
export function getTodayVienna() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Vienna' });
}

/** Converts a Date object to YYYY-MM-DD string in Europe/Vienna timezone */
export function toDateStr(d) {
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Vienna' });
}

/** Helper: go back N months from a 1-indexed month/year, return { von, bis: today } */
function monthsBack(n, year, month, today) {
  let sm = month - n;
  let sy = year;
  while (sm <= 0) { sm += 12; sy--; }
  return { von: `${sy}-${pad2(sm)}-01`, bis: today };
}

/**
 * Returns { von, bis } date range strings for filter presets.
 * All calculations use Europe/Vienna timezone.
 */
export function getDateRange(preset) {
  const today = getTodayVienna();
  const [year, month] = today.split('-').map(Number);

  switch (preset) {
    case 'heute':
      return { von: today, bis: today };

    case 'gestern': {
      const d = new Date(today + 'T12:00:00');
      d.setDate(d.getDate() - 1);
      const g = formatDateLocal(d);
      return { von: g, bis: g };
    }

    case 'diese_woche': {
      const d = new Date(today + 'T12:00:00');
      const dow = d.getDay(); // 0=So, 1=Mo, ...
      const diff = dow === 0 ? 6 : dow - 1;
      const montag = new Date(d);
      montag.setDate(montag.getDate() - diff);
      return { von: formatDateLocal(montag), bis: today };
    }

    // Keep old alias
    case 'woche':
      return getDateRange('diese_woche');

    case '30T': {
      const d = new Date(today + 'T12:00:00');
      d.setDate(d.getDate() - 30);
      return { von: formatDateLocal(d), bis: today };
    }

    case 'monat':
      return { von: `${year}-${pad2(month)}-01`, bis: today };

    case 'letzter_monat': {
      const lm = month === 1 ? 12 : month - 1;
      const ly = month === 1 ? year - 1 : year;
      const lastDay = new Date(ly, lm, 0).getDate();
      return { von: `${ly}-${pad2(lm)}-01`, bis: `${ly}-${pad2(lm)}-${pad2(lastDay)}` };
    }

    case '3m':
    case '3monate':
      return monthsBack(2, year, month, today);

    case '6m':
    case '6monate':
      return monthsBack(5, year, month, today);

    case '12m':
      return monthsBack(11, year, month, today);

    case 'jahr':
      return { von: `${year}-01-01`, bis: today };

    case 'gesamt':
      return { von: '2020-01-01', bis: today };

    default:
      return { von: today, bis: today };
  }
}

/**
 * Returns { von, bis } for a full calendar month.
 * If month/year equals current month, bis = today instead of end of month.
 */
export function getMonthRange(year, month) {
  const today = getTodayVienna();
  const [ty, tm] = today.split('-').map(Number);
  const lastDay = new Date(year, month, 0).getDate();
  const bis = (year === ty && month === tm) ? today : `${year}-${pad2(month)}-${pad2(lastDay)}`;
  return { von: `${year}-${pad2(month)}-01`, bis };
}

/**
 * Returns the last day of a given month as YYYY-MM-DD.
 * Safe alternative to new Date(y, m, 0).toISOString().slice(0,10).
 */
export function lastDayOfMonth(year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${pad2(month)}-${pad2(lastDay)}`;
}

/** Format a Date object as YYYY-MM-DD using local components (no timezone conversion) */
function formatDateLocal(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
